// IIT-H Recruitment Exam — Public Facial Recognition Kiosk
// Session-aware: Manage Sessions + Kiosk Verify scoped per session + Reports
// React.createElement throughout (avoids Babel deep-JSX stack overflow).
import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  CheckCircle2, XCircle, Camera, RefreshCw, ChevronLeft, ChevronRight, Upload, ShieldCheck,
  AlertTriangle, Database, FileDown, ScanFace, GraduationCap, Briefcase, Award,
  Users, Mail, Phone, ListChecks, Plus, Trash2, Clock, Calendar, BarChart3, FileText,
  SwitchCamera
} from "lucide-react";

const h = React.createElement;
const API_URL = process.env.REACT_APP_BACKEND_URL + "/api/face";

const LOGO_FALLBACK = "data:image/svg+xml;utf8," + encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">' +
  '<rect width="64" height="64" rx="10" fill="#1e293b"/>' +
  '<circle cx="32" cy="26" r="11" fill="none" stroke="#fbbf24" stroke-width="2"/>' +
  '<text x="32" y="31" text-anchor="middle" font-family="serif" font-weight="700" font-size="11" fill="#fbbf24">IITH</text>' +
  '<text x="32" y="56" text-anchor="middle" font-family="sans-serif" font-size="6" fill="white">SINCE 2008</text>' +
  '</svg>'
);

const SESSION_KEY = "iith.face.kiosk.session_id";

function BrandHeader({ subtitle, action }) {
  return h("header", { className: "bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white border-b-4 border-amber-400 shadow-lg" },
    h("div", { className: "max-w-7xl mx-auto px-6 py-4 flex items-center gap-4" },
      h("img", { src: LOGO_FALLBACK, alt: "IIT Hyderabad", className: "w-14 h-14 bg-white rounded-md p-1 shadow-md" }),
      h("div", { className: "flex-1 min-w-0" },
        h("p", { className: "text-[11px] uppercase tracking-[0.2em] text-amber-300 font-semibold" }, "Indian Institute of Technology Hyderabad"),
        h("h1", { className: "text-lg md:text-xl font-bold leading-tight", "data-testid": "kiosk-title" }, "Recruitment Examination \u2014 Hall Entry Verification"),
        subtitle && h("p", { className: "text-xs text-slate-300" }, subtitle)
      ),
      action
    )
  );
}

// Pretty datetime helpers
const fmtDT = (iso) => iso ? new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "\u2014";
const toISOForInput = (d) => { const z = new Date(d.getTime() - d.getTimezoneOffset() * 60000); return z.toISOString().slice(0, 16); };

const phaseColors = {
  active: "bg-emerald-100 text-emerald-700 border-emerald-200",
  scheduled: "bg-amber-100 text-amber-700 border-amber-200",
  ended: "bg-slate-100 text-slate-500 border-slate-200",
};
const phaseLabel = { active: "Live now", scheduled: "Starts soon", ended: "Closed" };

// Relative humanised window — "Starts in 2 h", "Ends in 38 min", "Ended 4 h ago"
function relativeWindow(s) {
  const now = Date.now();
  const start = new Date(s.start_at).getTime();
  const end = new Date(s.end_at).getTime();
  const fmt = (ms) => {
    const abs = Math.abs(ms);
    const m = Math.round(abs / 60000);
    if (m < 60) return m + " min";
    const h = Math.round(m / 60);
    if (h < 48) return h + " h";
    return Math.round(h / 24) + " d";
  };
  if (now < start) return "Starts in " + fmt(start - now);
  if (now > end) return "Ended " + fmt(now - end) + " ago";
  return "Ends in " + fmt(end - now);
}

// Shared picker body — used inside dialog AND as full-page landing
function SessionPickerBody({ sessions, loading, showNew, setShowNew, name, setName, position, setPosition, startAt, setStartAt, endAt, setEndAt, saving, onCreate, onSelected, ctaLabel }) {
  if (showNew) {
    return h("div", { className: "space-y-3" },
      h("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-3" },
        h("div", null,
          h(Label, { htmlFor: "sess-name" }, "Session Name *"),
          h(Input, { id: "sess-name", value: name, onChange: e => setName(e.target.value), placeholder: "e.g. JE Recruitment \u2014 Batch 1", "data-testid": "session-name-input" })
        ),
        h("div", null,
          h(Label, { htmlFor: "sess-position" }, "Position Name"),
          h(Input, { id: "sess-position", value: position || "", onChange: e => setPosition && setPosition(e.target.value), placeholder: "e.g. Junior Engineer (Civil)", "data-testid": "session-position-input" })
        )
      ),
      h("div", { className: "grid grid-cols-2 gap-3" },
        h("div", null,
          h(Label, { htmlFor: "sess-start" }, "Start Date & Time *"),
          h(Input, { id: "sess-start", type: "datetime-local", value: startAt, onChange: e => setStartAt(e.target.value), "data-testid": "session-start-input" })
        ),
        h("div", null,
          h(Label, { htmlFor: "sess-end" }, "End Date & Time *"),
          h(Input, { id: "sess-end", type: "datetime-local", value: endAt, onChange: e => setEndAt(e.target.value), "data-testid": "session-end-input" })
        )
      ),
      h("p", { className: "text-[11px] text-slate-500" },
        "Scanning is permitted only inside this window. Roster and scan logs stay scoped to this session."
      ),
      h("div", { className: "flex items-center justify-end gap-2 pt-2" },
        h(Button, { variant: "outline", onClick: () => setShowNew(false) }, "Back"),
        h(Button, { onClick: onCreate, disabled: saving, className: "bg-slate-900 hover:bg-slate-800", "data-testid": "session-create-confirm" },
          saving ? "Creating\u2026" : "Create Session"
        )
      )
    );
  }
  // Sort: active first, then scheduled, then ended
  const order = { active: 0, scheduled: 1, ended: 2 };
  const sorted = [...sessions].sort((a, b) => (order[a.phase] - order[b.phase]) || (new Date(b.start_at) - new Date(a.start_at)));
  return h("div", { className: "space-y-3" },
    h("button", {
      onClick: () => setShowNew(true),
      className: "w-full p-4 rounded-xl border-2 border-dashed border-amber-300 bg-amber-50/40 hover:bg-amber-50 hover:border-amber-400 transition-all flex items-center justify-between group",
      "data-testid": "create-session-btn"
    },
      h("div", { className: "flex items-center gap-3 text-left" },
        h("div", { className: "w-10 h-10 rounded-full bg-amber-400 text-slate-900 flex items-center justify-center font-bold shadow-md group-hover:scale-110 transition-transform" },
          h(Plus, { className: "w-5 h-5" })
        ),
        h("div", null,
          h("p", { className: "font-semibold text-slate-900 text-sm" }, "Create a new exam session"),
          h("p", { className: "text-xs text-slate-500" }, "Sets a name, start & end time. Roster lives inside that window.")
        )
      ),
      h("span", { className: "text-xs text-amber-700 font-medium hidden md:inline" }, "Open form \u2192")
    ),
    loading && h("p", { className: "text-xs text-slate-500 text-center py-4" }, "Loading sessions\u2026"),
    !loading && sorted.length === 0 && h("div", { className: "text-center py-10 border-2 border-dashed border-slate-200 rounded-xl" },
      h(Calendar, { className: "w-10 h-10 mx-auto text-slate-300 mb-2" }),
      h("p", { className: "text-sm text-slate-500" }, "No sessions yet"),
      h("p", { className: "text-[11px] text-slate-400" }, "Create your first session above to start indexing applicants.")
    ),
    !loading && sorted.length > 0 && h("div", { className: "space-y-2" },
      sorted.map(s => h("button", {
        key: s.id,
        onClick: () => onSelected(s),
        className: "w-full text-left p-4 rounded-xl border-2 transition-all hover:shadow-md " + (s.phase === "active" ? "border-emerald-300 bg-emerald-50/30 hover:border-emerald-500" : s.phase === "scheduled" ? "border-amber-200 bg-white hover:border-amber-400" : "border-slate-200 bg-slate-50/50 hover:border-slate-400 opacity-80"),
        "data-testid": "session-pick-" + s.id
      },
        h("div", { className: "flex items-start justify-between gap-3" },
          h("div", { className: "min-w-0 flex-1" },
            h("div", { className: "flex items-center gap-2 mb-1" },
              s.phase === "active"
                ? h("span", { className: "flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border bg-emerald-100 text-emerald-700 border-emerald-200" },
                    h("span", { className: "w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" }), phaseLabel.active
                  )
                : h("span", { className: "text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border " + phaseColors[s.phase] }, phaseLabel[s.phase] || s.phase),
              h("span", { className: "text-[10px] text-slate-500 font-mono" }, relativeWindow(s))
            ),
            h("p", { className: "font-semibold text-slate-900 truncate" }, s.name),
            h("p", { className: "text-[11px] text-slate-500 mt-0.5 flex items-center gap-1" },
              h(Clock, { className: "w-3 h-3 shrink-0" }),
              fmtDT(s.start_at) + " \u2192 " + fmtDT(s.end_at)
            ),
            h("div", { className: "mt-2 flex items-center gap-3 text-[11px] text-slate-600" },
              h("span", { className: "flex items-center gap-1" }, h(Users, { className: "w-3 h-3 text-slate-400" }),
                h("b", null, s.roster_count), " in roster"
              ),
              h("span", { className: "flex items-center gap-1" }, h(CheckCircle2, { className: "w-3 h-3 text-emerald-500" }),
                h("b", null, s.scans_matched), " verified"
              ),
              s.scans_unmatched > 0 && h("span", { className: "flex items-center gap-1" }, h(XCircle, { className: "w-3 h-3 text-rose-400" }),
                h("b", null, s.scans_unmatched), " unmatched"
              )
            )
          ),
          h("span", { className: "shrink-0 inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg " + (s.phase === "active" ? "bg-slate-900 text-amber-300" : "bg-slate-100 text-slate-600") },
            ctaLabel || "Open", h(ChevronRight, { className: "w-3 h-3" })
          )
        )
      ))
    )
  );
}


function SessionPickerDialog({ open, onClose, onSelected, mode }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [name, setName] = useState("");
  const [position, setPosition] = useState("");
  const [startAt, setStartAt] = useState(() => toISOForInput(new Date()));
  const [endAt, setEndAt] = useState(() => toISOForInput(new Date(Date.now() + 4 * 3600 * 1000)));
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(API_URL + "/sessions");
      setSessions(r.data.items || []);
    } catch (_) { setSessions([]); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { if (open) refresh(); }, [open, refresh]);

  const onCreate = async () => {
    if (!name.trim()) { toast.error("Session name is required"); return; }
    if (!startAt || !endAt) { toast.error("Start and End are required"); return; }
    setSaving(true);
    try {
      const r = await axios.post(API_URL + "/sessions", {
        name: name.trim(),
        position_name: (position || "").trim(),
        start_at: new Date(startAt).toISOString(),
        end_at: new Date(endAt).toISOString(),
      });
      toast.success("Session created");
      setShowNew(false); setName(""); setPosition("");
      onSelected(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create");
    } finally { setSaving(false); }
  };

  return h(Dialog, { open, onOpenChange: onClose },
    h(DialogContent, { className: "max-w-2xl max-h-[85vh] overflow-y-auto", "data-testid": "session-picker-dialog" },
      h(DialogHeader, null,
        h(DialogTitle, { className: "flex items-center gap-2" },
          h(Calendar, { className: "w-5 h-5 text-amber-500" }),
          mode === "kiosk" ? "Switch Exam Session" : "Select a Session to Manage"
        )
      ),
      h("div", { className: "mt-4" },
        h(SessionPickerBody, {
          sessions, loading, showNew, setShowNew,
          name, setName, position, setPosition, startAt, setStartAt, endAt, setEndAt,
          saving, onCreate, onSelected,
          ctaLabel: mode === "kiosk" ? "Start Verify" : "Manage"
        })
      )
    )
  );
}

// ============================================================================
// Session Landing — full-page session picker used as the kiosk entry screen
// ============================================================================
function SessionLanding({ onSelected, mode = "kiosk" }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [name, setName] = useState("");
  const [position, setPosition] = useState("");
  const [startAt, setStartAt] = useState(() => toISOForInput(new Date()));
  const [endAt, setEndAt] = useState(() => toISOForInput(new Date(Date.now() + 4 * 3600 * 1000)));
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try { const r = await axios.get(API_URL + "/sessions"); setSessions(r.data.items || []); }
    catch (_) { setSessions([]); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const onCreate = async () => {
    if (!name.trim()) { toast.error("Session name is required"); return; }
    if (!startAt || !endAt) { toast.error("Start and End are required"); return; }
    setSaving(true);
    try {
      const r = await axios.post(API_URL + "/sessions", {
        name: name.trim(),
        position_name: (position || "").trim(),
        start_at: new Date(startAt).toISOString(),
        end_at: new Date(endAt).toISOString(),
      });
      toast.success("Session created");
      setShowNew(false); setName(""); setPosition("");
      onSelected(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create");
    } finally { setSaving(false); }
  };

  const activeCount = sessions.filter(s => s.phase === "active").length;
  const scheduledCount = sessions.filter(s => s.phase === "scheduled").length;
  const endedCount = sessions.filter(s => s.phase === "ended").length;

  return h("div", { className: "min-h-screen bg-slate-100 flex flex-col" },
    h(BrandHeader, {
      subtitle: mode === "kiosk" ? "Step 1 of 2 \u00B7 Choose the exam session to begin verifying candidates" : "Manage exam sessions",
      action: mode === "kiosk" && h(Link, {
        to: "/exam-entry/manage",
        className: "hidden md:inline-flex items-center gap-1 text-xs text-amber-300 hover:text-amber-200 px-3 py-1.5 rounded border border-amber-300/30"
      }, h(Database, { className: "w-3 h-3" }), " Manage")
    }),
    h("main", { className: "flex-1 max-w-4xl mx-auto w-full px-6 py-8" },
      // Hero
      h("div", { className: "mb-6 text-center" },
        h("div", { className: "inline-flex items-center justify-center w-14 h-14 rounded-full bg-amber-100 text-amber-600 mb-3 shadow-sm" },
          h(Calendar, { className: "w-7 h-7" })
        ),
        h("h2", { className: "text-2xl md:text-3xl font-bold text-slate-900" },
          mode === "kiosk" ? "Choose an Exam Session" : "Pick a session to manage"
        ),
        h("p", { className: "text-sm text-slate-600 mt-1 max-w-xl mx-auto" },
          mode === "kiosk"
            ? "Each exam batch runs as its own session with a strict time window. Pick the one that matches today's exam, or create a new one."
            : "Roster uploads, scans, and reports are scoped to a session. Pick one to load its data."
        )
      ),
      // Quick-stats bar
      sessions.length > 0 && h("div", { className: "grid grid-cols-3 gap-3 mb-6" },
        h("div", { className: "p-3 rounded-xl border border-emerald-200 bg-emerald-50/60" },
          h("p", { className: "text-[10px] uppercase tracking-wider text-emerald-700 font-semibold" }, "Live Now"),
          h("p", { className: "text-2xl font-bold text-emerald-700 mt-0.5" }, activeCount)
        ),
        h("div", { className: "p-3 rounded-xl border border-amber-200 bg-amber-50/60" },
          h("p", { className: "text-[10px] uppercase tracking-wider text-amber-700 font-semibold" }, "Scheduled"),
          h("p", { className: "text-2xl font-bold text-amber-700 mt-0.5" }, scheduledCount)
        ),
        h("div", { className: "p-3 rounded-xl border border-slate-200 bg-white" },
          h("p", { className: "text-[10px] uppercase tracking-wider text-slate-500 font-semibold" }, "Ended"),
          h("p", { className: "text-2xl font-bold text-slate-500 mt-0.5" }, endedCount)
        )
      ),
      h(Card, { className: "shadow-lg" }, h(CardContent, { className: "p-6" },
        h(SessionPickerBody, {
          sessions, loading, showNew, setShowNew,
          name, setName, position, setPosition, startAt, setStartAt, endAt, setEndAt,
          saving, onCreate, onSelected,
          ctaLabel: mode === "kiosk" ? "Start Verify" : "Manage"
        })
      )),
      h("p", { className: "text-[11px] text-slate-400 text-center mt-4 flex items-center justify-center gap-1.5" },
        h(ShieldCheck, { className: "w-3 h-3 text-emerald-500" }),
        "Scanning is gated by the session's time window. Every scan and attempt is securely logged for audit."
      )
    )
  );
}



// ============================================================================
// Match details panel
// ============================================================================
function MatchDetailsPanel({ result }) {
  const cand = result.candidate || {};
  const initials = (cand.name || "?").split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();
  return h(Card, { className: "border-2 border-emerald-500 shadow-2xl overflow-hidden", "data-testid": "kiosk-result-match" },
    h("div", { className: "bg-gradient-to-r from-emerald-500 to-emerald-600 text-white px-5 py-3 flex items-center justify-between" },
      h("div", { className: "flex items-center gap-2" },
        h(CheckCircle2, { className: "w-5 h-5" }),
        h("span", { className: "font-bold uppercase tracking-wider text-sm" }, "Authorised \u00B7 Entry Permitted")
      ),
      h("div", { className: "flex items-center gap-2" },
        result.latency_ms && h("span", { className: "text-[10px] bg-white/20 px-1.5 py-0.5 rounded font-mono" }, result.latency_ms + " ms"),
        h(Badge, { className: "bg-white text-emerald-700 font-mono shadow" }, (result.similarity || 0).toFixed(2) + "%")
      )
    ),
    h(CardContent, { className: "p-0" },
      h("div", { className: "p-5 grid grid-cols-[120px_1fr] gap-4 items-start bg-white" },
        h("div", { className: "rounded-xl overflow-hidden border-2 border-emerald-200 shadow-md bg-slate-100 w-[120px] h-[150px] flex items-center justify-center" },
          cand.photo_url
            ? h("img", {
                src: cand.photo_url, alt: cand.name, className: "w-full h-full object-cover",
                onError: (e) => { e.currentTarget.style.display = "none"; e.currentTarget.parentElement.innerHTML = '<span class="text-3xl font-bold text-slate-400">' + initials + '</span>'; },
                "data-testid": "match-photo"
              })
            : h("span", { className: "text-3xl font-bold text-slate-400" }, initials)
        ),
        h("div", { className: "min-w-0" },
          h("p", { className: "text-xs font-mono text-slate-400 uppercase tracking-wider" }, cand.application_no || "\u2014"),
          h("p", { className: "text-xl md:text-2xl font-bold text-slate-900 leading-tight", "data-testid": "kiosk-result-name" }, cand.name || "\u2014"),
          h("div", { className: "mt-2 flex flex-wrap gap-1.5 text-xs" },
            cand.category && h(Badge, { variant: "outline", className: "border-emerald-300 text-emerald-700" }, cand.category),
            cand.total_experience && h(Badge, { variant: "outline", className: "border-blue-300 text-blue-700" }, "Exp: " + cand.total_experience)
          ),
          h("div", { className: "mt-3 space-y-1 text-xs text-slate-600" },
            cand.email && h("div", { className: "flex items-center gap-1.5" }, h(Mail, { className: "w-3 h-3" }), h("span", { className: "truncate" }, cand.email)),
            cand.mobile && h("div", { className: "flex items-center gap-1.5" }, h(Phone, { className: "w-3 h-3" }), h("span", null, cand.mobile))
          )
        )
      ),
      (cand.education || []).length > 0 && h("div", { className: "px-5 py-3 border-t border-slate-100 bg-slate-50/50" },
        h("p", { className: "text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2 flex items-center gap-1" },
          h(GraduationCap, { className: "w-3 h-3" }), "Education (" + cand.education.length + ")"
        ),
        h("div", { className: "space-y-1.5 max-h-40 overflow-y-auto" },
          cand.education.slice(0, 4).map((e, i) => h("div", { key: i, className: "text-xs bg-white border border-slate-200 rounded px-2 py-1.5" },
            h("p", { className: "font-medium text-slate-800" }, e.exam || e.edu_level),
            h("p", { className: "text-slate-500 text-[11px]" },
              [e.edu_level !== (e.exam || e.edu_level) ? e.edu_level : null, e.specialization, e.year_passing ? "Year " + e.year_passing : null, e.pct ? e.pct + "%" : null].filter(Boolean).join(" \u00B7 ")
            )
          ))
        )
      ),
      (cand.experience || []).length > 0 && h("div", { className: "px-5 py-3 border-t border-slate-100 bg-slate-50/50" },
        h("p", { className: "text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2 flex items-center gap-1" },
          h(Briefcase, { className: "w-3 h-3" }), "Experience (" + cand.experience.length + ")"
        ),
        h("div", { className: "space-y-1.5 max-h-40 overflow-y-auto" },
          cand.experience.slice(0, 4).map((e, i) => h("div", { key: i, className: "text-xs bg-white border border-slate-200 rounded px-2 py-1.5" },
            h("p", { className: "font-medium text-slate-800 truncate" }, e.post),
            h("p", { className: "text-slate-500 text-[11px] truncate" }, e.organization),
            (e.from || e.to || e.service_duration) && h("p", { className: "text-slate-400 text-[10px]" },
              [e.from, e.to].filter(Boolean).join(" \u2192 ") || e.service_duration
            )
          ))
        )
      ),
      (cand.achievements || []).length > 0 && h("div", { className: "px-5 py-3 border-t border-slate-100 bg-slate-50/50" },
        h("p", { className: "text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2 flex items-center gap-1" },
          h(Award, { className: "w-3 h-3" }), "Achievements (" + cand.achievements.length + ")"
        ),
        h("div", { className: "space-y-1 text-xs max-h-32 overflow-y-auto" },
          cand.achievements.slice(0, 4).map((a, i) => h("div", { key: i, className: "bg-white border border-slate-200 rounded px-2 py-1" },
            h("span", { className: "font-medium text-slate-700" }, a.title),
            a.authority && h("span", { className: "text-slate-500" }, " \u2014 " + a.authority)
          ))
        )
      )
    )
  );
}

// ============================================================================
// Summary tiles + scan log modal
// ============================================================================
function SummaryTiles({ summary, onTileClick }) {
  const tiles = [
    { key: "scans_total", label: "Total Scanned", icon: ScanFace, color: "slate", filter: null },
    { key: "scans_matched", label: "Matched", icon: CheckCircle2, color: "emerald", filter: "MATCH" },
    { key: "scans_unmatched", label: "Unmatched", icon: XCircle, color: "rose", filter: "NO_MATCH" },
    { key: "roster_count", label: "In Roster", icon: Users, color: "amber", filter: "ROSTER" },
  ];
  const colorMap = {
    slate: "bg-slate-50 border-slate-200 hover:border-slate-400 text-slate-700",
    emerald: "bg-emerald-50 border-emerald-200 hover:border-emerald-400 text-emerald-700",
    rose: "bg-rose-50 border-rose-200 hover:border-rose-400 text-rose-700",
    amber: "bg-amber-50 border-amber-200 hover:border-amber-400 text-amber-700",
  };
  return h("div", { className: "grid grid-cols-2 md:grid-cols-4 gap-3" },
    tiles.map(t => h("button", {
      key: t.key,
      onClick: () => onTileClick && onTileClick(t.filter, t.label),
      className: "text-left p-4 rounded-xl border-2 transition-all hover:shadow-md " + colorMap[t.color],
      "data-testid": "summary-tile-" + t.key
    },
      h("div", { className: "flex items-start justify-between" },
        h("div", null,
          h("p", { className: "text-[10px] uppercase tracking-wider font-semibold opacity-75" }, t.label),
          h("p", { className: "text-3xl font-bold mt-1", "data-testid": "summary-value-" + t.key }, (summary[t.key] ?? 0).toLocaleString())
        ),
        h(t.icon, { className: "w-5 h-5 opacity-50" })
      ),
      h("p", { className: "text-[10px] opacity-60 mt-2" }, "Click for details \u2192")
    ))
  );
}

function ScanLogModal({ open, onClose, filter, label, sessionId }) {
  const [logs, setLogs] = useState([]);
  const [roster, setRoster] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open || !sessionId) return;
    setLoading(true);
    if (filter === "ROSTER") {
      axios.get(API_URL + "/candidates?session_id=" + sessionId + "&limit=500")
        .then(r => setRoster(r.data.items || [])).catch(() => setRoster([])).finally(() => setLoading(false));
    } else {
      const url = API_URL + "/scan-logs?session_id=" + sessionId + "&limit=500" + (filter ? "&status=" + filter : "");
      axios.get(url).then(r => setLogs(r.data.items || [])).catch(() => setLogs([])).finally(() => setLoading(false));
    }
  }, [open, filter, sessionId]);

  const body = filter === "ROSTER"
    ? h("div", { className: "overflow-x-auto" },
        roster.length === 0
          ? h("p", { className: "text-sm text-slate-500 italic p-8 text-center" }, loading ? "Loading\u2026" : "No applicants in roster.")
          : h("table", { className: "w-full text-sm" },
            h("thead", null, h("tr", { className: "text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50" },
              h("th", { className: "px-3 py-2" }, "Application No"),
              h("th", { className: "px-3 py-2" }, "Name"),
              h("th", { className: "px-3 py-2" }, "Category"),
              h("th", { className: "px-3 py-2" }, "Mobile"),
              h("th", { className: "px-3 py-2" }, "Experience"),
              h("th", { className: "px-3 py-2" }, "Indexed")
            )),
            h("tbody", { className: "divide-y divide-slate-100" }, roster.map(c =>
              h("tr", { key: c.application_no, className: "hover:bg-slate-50" },
                h("td", { className: "px-3 py-2 font-mono text-xs" }, c.application_no),
                h("td", { className: "px-3 py-2 font-medium" }, c.name),
                h("td", { className: "px-3 py-2 text-xs" }, c.category || "\u2014"),
                h("td", { className: "px-3 py-2 text-xs" }, c.mobile || "\u2014"),
                h("td", { className: "px-3 py-2 text-xs" }, c.total_experience || "\u2014"),
                h("td", { className: "px-3 py-2 text-xs text-slate-500" }, new Date(c.indexed_at).toLocaleString("en-IN"))
              ))
            )
          )
      )
    : h("div", { className: "overflow-x-auto" },
        logs.length === 0
          ? h("p", { className: "text-sm text-slate-500 italic p-8 text-center" }, loading ? "Loading\u2026" : "No scans recorded yet.")
          : h("table", { className: "w-full text-sm" },
            h("thead", null, h("tr", { className: "text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50" },
              h("th", { className: "px-3 py-2" }, "Time"),
              h("th", { className: "px-3 py-2" }, "Status"),
              h("th", { className: "px-3 py-2" }, "Application No"),
              h("th", { className: "px-3 py-2" }, "Name"),
              h("th", { className: "px-3 py-2" }, "Similarity")
            )),
            h("tbody", { className: "divide-y divide-slate-100" }, logs.map(l =>
              h("tr", { key: l.scan_id, className: "hover:bg-slate-50" },
                h("td", { className: "px-3 py-2 text-xs text-slate-500" }, new Date(l.scan_timestamp).toLocaleString("en-IN")),
                h("td", { className: "px-3 py-2" },
                  l.status === "MATCH" ? h(Badge, { className: "bg-emerald-100 text-emerald-700 text-[10px]" }, "Match")
                  : l.status === "DUPLICATE" ? h(Badge, { className: "bg-amber-100 text-amber-700 text-[10px]" }, "Duplicate")
                  : l.status === "NO_MATCH" ? h(Badge, { className: "bg-rose-100 text-rose-700 text-[10px]" }, "No Match")
                  : h(Badge, { className: "bg-amber-100 text-amber-700 text-[10px]" }, l.status)
                ),
                h("td", { className: "px-3 py-2 font-mono text-xs" }, l.matched_application_no || "\u2014"),
                h("td", { className: "px-3 py-2 text-xs" }, l.matched_name || "\u2014"),
                h("td", { className: "px-3 py-2 text-xs" }, l.similarity != null ? l.similarity.toFixed(2) + "%" : "\u2014")
              ))
            )
          )
      );

  return h(Dialog, { open, onOpenChange: onClose },
    h(DialogContent, { className: "max-w-4xl max-h-[80vh] overflow-y-auto", "data-testid": "scan-log-modal" },
      h(DialogHeader, null, h(DialogTitle, { className: "flex items-center gap-2" },
        h(ListChecks, { className: "w-5 h-5 text-slate-500" }), label || "Details"
      )),
      body
    )
  );
}

// ============================================================================
// Kiosk Verify Screen — session-aware
// ============================================================================
export function FaceVerifyKiosk() {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [session, setSession] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [streamReady, setStreamReady] = useState(false);
  const [streamError, setStreamError] = useState(null);
  const [facingMode, setFacingMode] = useState("user"); // 'user' = front, 'environment' = back
  const [hasMultipleCameras, setHasMultipleCameras] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [summary, setSummary] = useState({ scans_total: 0, scans_matched: 0, scans_unmatched: 0, roster_count: 0 });
  const [modal, setModal] = useState({ open: false, filter: null, label: "" });
  const navigate = useNavigate();

  // No silent auto-restore from storage — the operator must explicitly pick a
  // session every time the kiosk page is opened, otherwise the wrong batch's
  // candidates would get verified by accident.

  const onSessionSelected = (s) => { setSession(s); setPickerOpen(false); };
  const switchSession = () => { setSession(null); setResult(null); setPickerOpen(false); };

  // Camera lifecycle
  const startCamera = useCallback(async () => {
    setStreamError(null);
    setStreamReady(false);
    // Tear down any previous stream before requesting a new one (flip camera)
    if (streamRef.current) {
      try { streamRef.current.getTracks().forEach(t => t.stop()); } catch (_) {}
      streamRef.current = null;
    }
    // Pre-flight checks — surface actionable messages instead of a padlock
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setStreamError("This browser doesn't support camera access. Please use Chrome, Edge or Firefox.");
      return;
    }
    if (!window.isSecureContext) {
      setStreamError("Camera requires HTTPS. Open the site via https:// (or ask IT to enable it) and reload.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: facingMode }, width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          // Guard: some browsers (Brave shields, corporate proxies) attach a
          // 0x0 dummy stream. If the actual dimensions are zero, treat as error.
          if (!videoRef.current.videoWidth || !videoRef.current.videoHeight) {
            setStreamError("Camera returned an empty video feed. Check that no other app is using the camera, and that browser 'Shields' aren't blocking it.");
            return;
          }
          setStreamReady(true);
        };
      }
      // Once permission is granted, enumerateDevices returns real labels and
      // we can tell whether the device has more than one camera to show the
      // flip button only when useful.
      try {
        const devs = await navigator.mediaDevices.enumerateDevices();
        const cams = devs.filter(d => d.kind === "videoinput");
        setHasMultipleCameras(cams.length > 1);
      } catch (_) { /* non-fatal */ }
    } catch (err) {
      // Map err.name -> actionable message
      const name = err?.name || "";
      let msg;
      if (name === "NotAllowedError" || name === "SecurityError") {
        msg = "Camera permission was denied. Click the camera icon in the address bar, allow access, then Retry.";
      } else if (name === "NotFoundError" || name === "OverconstrainedError") {
        msg = "No camera detected. Plug in a webcam or check Windows Camera privacy settings.";
      } else if (name === "NotReadableError") {
        msg = "Camera is being used by another app (Zoom / Teams / Meet). Close that app and Retry.";
      } else {
        msg = `Camera error: ${name || err?.message || "unknown"}`;
      }
      setStreamError(msg);
    }
  }, [facingMode]);
  const flipCamera = useCallback(() => {
    setFacingMode(prev => (prev === "user" ? "environment" : "user"));
  }, []);
  // Only (re)start the camera when the session ID or facing mode changes — NOT when polled
  // session details refresh (otherwise the 15-s poll tears down the stream).
  useEffect(() => {
    if (!session?.id) return;
    startCamera();
    return () => { if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop()); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.id, facingMode]);

  // Poll summary + session details every 15 s. Depend on session.id (stable
  // string) rather than the full session object so this interval isn't torn
  // down on every poll.
  const sessionId = session?.id;
  useEffect(() => {
    if (!sessionId) return;
    const tick = () => {
      axios.get(API_URL + "/summary?session_id=" + sessionId).then(r => setSummary(r.data)).catch(() => {});
      axios.get(API_URL + "/sessions/" + sessionId).then(r => setSession(prev => prev ? { ...prev, ...r.data } : r.data)).catch(() => {});
    };
    tick();
    const iv = setInterval(tick, 15000);
    return () => clearInterval(iv);
  }, [sessionId]);

  const captureAndVerify = useCallback(async () => {
    if (!videoRef.current || !canvasRef.current || busy || !session) return;
    setBusy(true); setResult(null);
    const v = videoRef.current; const c = canvasRef.current;
    const MAX_W = 720;
    const sw = v.videoWidth || 1280, sh = v.videoHeight || 720;
    const scale = Math.min(1, MAX_W / sw);
    c.width = Math.round(sw * scale); c.height = Math.round(sh * scale);
    c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
    const dataUrl = c.toDataURL("image/jpeg", 0.85);
    const t0 = performance.now();
    try {
      const r = await axios.post(API_URL + "/search", { image: dataUrl, session_id: session.id }, { timeout: 25000 });
      const ms = Math.round(performance.now() - t0);
      setResult({ ...r.data, latency_ms: ms });
      // refresh tile counts (not the whole session — that would restart the camera)
      axios.get(API_URL + "/summary?session_id=" + session.id).then(r2 => setSummary(r2.data)).catch(() => {});
    } catch (err) {
      setResult({ status: "ERROR", error: err.response?.data?.detail || err.message || "Verification failed" });
    } finally { setBusy(false); }
  }, [busy, session]);

  // Auto-clear match/duplicate result after 15s so kiosk is ready for the next candidate
  useEffect(() => {
    if (!result || (result.status !== "MATCH" && result.status !== "DUPLICATE")) return;
    const id = setTimeout(() => setResult(null), 15000);
    return () => clearTimeout(id);
  }, [result]);

  if (!session) {
    return h(SessionLanding, { onSelected: onSessionSelected, mode: "kiosk" });
  }

  const isMatch = result?.status === "MATCH";
  const isDuplicate = result?.status === "DUPLICATE";
  const isNoMatch = result?.status === "NO_MATCH";
  const isNoFace = result?.status === "NO_FACE";
  const isOutOfWindow = result?.status === "OUT_OF_WINDOW";
  const isError = result?.status === "ERROR";
  const sessionActive = session.phase === "active";

  const cameraBlock = h(Card, { className: "overflow-hidden shadow-xl" },
    h(CardContent, { className: "p-0" },
      h("div", { className: "relative bg-black aspect-[4/3] flex items-center justify-center", "data-testid": "kiosk-camera-wrapper" },
        h("video", { ref: videoRef, autoPlay: true, playsInline: true, muted: true, className: "w-full h-full object-cover " + (facingMode === "user" ? "scale-x-[-1]" : ""), "data-testid": "kiosk-video" }),
        h("canvas", { ref: canvasRef, className: "hidden" }),
        streamReady && !result && h("div", { className: "pointer-events-none absolute inset-0 flex items-center justify-center" },
          h("div", { className: "w-[55%] aspect-[3/4] rounded-[40%] border-2 border-amber-400/60 shadow-[0_0_40px_rgba(251,191,36,0.3)]" })
        ),
        !streamReady && !streamError && h("div", { className: "absolute inset-0 flex items-center justify-center text-white/80 bg-black/60" },
          h("div", { className: "text-center" }, h(Camera, { className: "w-10 h-10 mx-auto mb-2 animate-pulse" }), h("p", { className: "text-sm" }, "Connecting to camera\u2026"))
        ),
        streamError && h("div", { className: "absolute inset-0 flex items-center justify-center bg-black/80 text-white p-6 text-center" },
          h("div", null, h(AlertTriangle, { className: "w-12 h-12 mx-auto text-amber-400 mb-3" }),
            h("p", { className: "text-sm" }, streamError),
            h(Button, { onClick: startCamera, className: "mt-4", variant: "secondary" }, h(RefreshCw, { className: "w-3 h-3 mr-1" }), "Retry"))
        ),
        streamReady && !result && h("div", { className: "absolute top-4 right-4 flex items-center gap-2" },
          h("button", {
            type: "button",
            onClick: flipCamera,
            title: facingMode === "user" ? "Switch to back camera" : "Switch to front camera",
            "aria-label": "Flip camera",
            className: "h-9 w-9 rounded-full bg-black/55 hover:bg-black/75 active:scale-95 transition flex items-center justify-center text-white shadow-lg backdrop-blur-sm",
            "data-testid": "kiosk-flip-camera-btn"
          }, h(SwitchCamera, { className: "w-4 h-4" })),
          h("div", { className: "px-2 py-1 rounded-full bg-emerald-500/90 text-white text-[10px] font-semibold uppercase tracking-wider flex items-center gap-1" },
            h("span", { className: "w-1.5 h-1.5 rounded-full bg-white animate-pulse" }), " Live"
          )
        ),
        streamReady && !result && h("div", { className: "absolute bottom-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-black/50 text-white text-[10px] uppercase tracking-wider" },
          "Look straight at the camera \u00B7 Strict 90% match"
        )
      ),
      h("div", { className: "bg-slate-900 px-6 py-4 flex items-center justify-between gap-4" },
        h("div", { className: "text-white" },
          h("p", { className: "text-xs text-slate-400 uppercase tracking-wider" }, "Roster Loaded"),
          h("p", { className: "text-lg font-bold", "data-testid": "kiosk-roster-count" }, (summary.roster_count ?? 0).toLocaleString(), " ", h("span", { className: "text-xs text-slate-400 font-normal" }, "applicants"))
        ),
        h(Button, {
          onClick: captureAndVerify,
          disabled: !streamReady || busy || (summary.roster_count === 0) || !sessionActive,
          className: "h-14 px-8 text-base bg-amber-400 hover:bg-amber-500 text-slate-900 font-bold shadow-lg disabled:bg-slate-700 disabled:text-slate-500",
          "data-testid": "kiosk-verify-btn"
        }, h(ScanFace, { className: "w-6 h-6 mr-2" }), busy ? "Verifying\u2026" : (sessionActive ? "Tap to Verify" : (session.phase === "scheduled" ? "Session not yet started" : "Session ended")))
      )
    )
  );

  let resultBlock;
  if (!result) {
    resultBlock = h(Card, { className: "border-dashed border-2" }, h(CardContent, { className: "p-8 text-center text-slate-500" },
      h(ScanFace, { className: "w-16 h-16 mx-auto mb-3 text-slate-300" }),
      h("p", { className: "text-sm font-medium text-slate-700" }, "Awaiting candidate"),
      h("p", { className: "text-xs mt-1" }, "Position candidate within the oval guide and tap ", h("b", null, "Verify"), "."),
      summary.roster_count === 0 && h("div", { className: "mt-4 p-3 bg-rose-50 border border-rose-200 rounded-lg text-xs text-rose-700" },
        "\u26A0 No applicants loaded in this session. Go to ",
        h(Link, { to: "/exam-entry/manage", className: "underline font-semibold" }, "Manage"), " to upload roster."
      )
    ));
  } else if (isMatch) {
    resultBlock = h(MatchDetailsPanel, { result });
  } else if (isDuplicate) {
    const cand = result.candidate || {};
    const verifiedAt = result.first_verified_at ? new Date(result.first_verified_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "earlier";
    resultBlock = h(Card, { className: "border-2 border-amber-500 shadow-2xl overflow-hidden", "data-testid": "kiosk-result-duplicate" },
      h("div", { className: "bg-gradient-to-r from-amber-500 to-amber-600 text-white px-5 py-3 flex items-center justify-between" },
        h("div", { className: "flex items-center gap-2" },
          h(AlertTriangle, { className: "w-5 h-5" }),
          h("span", { className: "font-bold uppercase tracking-wider text-sm" }, "Already Verified \u00B7 Duplicate Scan")
        ),
        result.latency_ms && h("span", { className: "text-[10px] bg-white/20 px-1.5 py-0.5 rounded font-mono" }, result.latency_ms + " ms")
      ),
      h(CardContent, { className: "p-5 bg-white" },
        h("p", { className: "text-xs font-mono text-slate-400 uppercase tracking-wider" }, cand.application_no || "\u2014"),
        h("p", { className: "text-xl md:text-2xl font-bold text-slate-900 leading-tight", "data-testid": "kiosk-duplicate-name" }, cand.name || "\u2014"),
        h("div", { className: "mt-4 p-3 rounded-lg bg-amber-50 border border-amber-200" },
          h("p", { className: "text-sm font-semibold text-amber-800 flex items-center gap-1.5" },
            h(Clock, { className: "w-4 h-4" }), "First verified at ", verifiedAt
          ),
          h("p", { className: "text-xs text-amber-700 mt-1" }, "This applicant has already entered the examination hall in the current session. Refer to the invigilator for manual check. This duplicate attempt has been logged.")
        )
      )
    );
  } else if (isOutOfWindow) {
    resultBlock = h(Card, { className: "border-2 border-amber-500 shadow-xl", "data-testid": "kiosk-result-window" },
      h("div", { className: "bg-gradient-to-r from-amber-500 to-amber-600 text-white px-5 py-3 flex items-center gap-2" },
        h(Clock, { className: "w-5 h-5" }),
        h("span", { className: "font-bold uppercase tracking-wider text-sm" }, "Session not active")
      ),
      h(CardContent, { className: "p-6 text-center" }, h("p", { className: "text-sm text-slate-700" }, result.error))
    );
  } else if (isNoMatch) {
    resultBlock = h(Card, { className: "border-2 border-rose-500 shadow-xl", "data-testid": "kiosk-result-nomatch" },
      h("div", { className: "bg-gradient-to-r from-rose-500 to-rose-600 text-white px-5 py-3 flex items-center gap-2" },
        h(XCircle, { className: "w-5 h-5" }), h("span", { className: "font-bold uppercase tracking-wider text-sm" }, "Not Recognised \u00B7 Manual Check Required")
      ),
      h(CardContent, { className: "p-6 text-center" },
        h(XCircle, { className: "w-16 h-16 mx-auto text-rose-200 mb-2" }),
        h("p", { className: "text-lg font-semibold text-slate-800" }, "No matching applicant in roster"),
        h("p", { className: "text-sm text-slate-500 mt-2" }, "Please verify identity manually using ID and admit card.")
      )
    );
  } else if (isNoFace) {
    resultBlock = h(Card, { className: "border-2 border-amber-400", "data-testid": "kiosk-result-noface" }, h(CardContent, { className: "p-6 text-center" },
      h(AlertTriangle, { className: "w-12 h-12 mx-auto text-amber-500 mb-2" }),
      h("p", { className: "text-xl font-semibold text-amber-700" }, "No Face Detected"),
      h("p", { className: "text-sm text-slate-500 mt-1" }, result.error)
    ));
  } else if (isError) {
    resultBlock = h(Card, { className: "border-rose-300" }, h(CardContent, { className: "p-5 text-center text-rose-700" },
      h("p", { className: "font-semibold" }, "Verification error"),
      h("p", { className: "text-xs" }, result.error)
    ));
  }

  const phaseBadge = h(Badge, {
    className: (session.phase === "active" ? "bg-emerald-100 text-emerald-700"
      : session.phase === "scheduled" ? "bg-amber-100 text-amber-700"
      : "bg-slate-100 text-slate-500") + " border text-[10px] uppercase"
  }, session.phase);

  return h("div", { className: "min-h-screen bg-slate-100 flex flex-col" },
    h(BrandHeader, {
      subtitle: "Session: " + session.name + " \u00B7 " + fmtDT(session.start_at) + " \u2192 " + fmtDT(session.end_at),
      action: h("div", { className: "flex items-center gap-2" },
        phaseBadge,
        h(Link, { to: "/exam-entry/manage", className: "hidden md:inline-flex items-center gap-1 text-xs text-amber-300 hover:text-amber-200 px-3 py-1.5 rounded border border-amber-300/30" },
          h(Database, { className: "w-3 h-3" }), " Manage"
        ),
        h(Button, { variant: "ghost", size: "sm", onClick: switchSession, className: "text-amber-300 hover:text-amber-200 hover:bg-white/10", "data-testid": "switch-session-btn" },
          h(RefreshCw, { className: "w-3 h-3 mr-1" }), "Switch Session"
        )
      )
    }),
    h("main", { className: "flex-1 max-w-7xl mx-auto w-full px-6 py-5 space-y-5" },
      h(SummaryTiles, { summary, onTileClick: (filter, label) => setModal({ open: true, filter, label }) }),
      h("div", { className: "grid grid-cols-1 lg:grid-cols-5 gap-5" },
        h("section", { className: "lg:col-span-3" }, cameraBlock,
          h("div", { className: "mt-3 flex items-center justify-between text-xs text-slate-500 px-1" },
            h("span", { className: "flex items-center gap-1" }, h(ShieldCheck, { className: "w-3 h-3 text-emerald-600" }), "Captured frames stored securely for audit"),
            h(Link, { to: "/exam-entry/manage", className: "hover:text-slate-700 underline md:hidden" }, "Manage")
          )
        ),
        h("section", { className: "lg:col-span-2" }, h("div", { className: "sticky top-5 space-y-3" }, resultBlock))
      )
    ),
    h(ScanLogModal, { open: modal.open, onClose: () => setModal({ open: false, filter: null, label: "" }), filter: modal.filter, label: modal.label, sessionId: session.id }),
    h(SessionPickerDialog, { open: pickerOpen, onClose: () => setPickerOpen(false), onSelected: onSessionSelected, mode: "kiosk" })
  );
}

// ============================================================================
// Manage Database screen — Sessions list + per-session upload
// ============================================================================
// ============================================================================
// Manage Kiosk — sessions dashboard, roster upload, delete
// ============================================================================
function JobDetailsDialog({ job, onClose }) {
  const [detail, setDetail] = useState(null);
  useEffect(() => {
    if (!job?.id) return;
    let alive = true;
    const tick = async () => {
      try {
        const r = await axios.get(API_URL + "/upload-jobs/" + job.id);
        if (alive) setDetail(r.data);
        if (alive && r.data.status !== "done" && r.data.status !== "error") setTimeout(tick, 2000);
      } catch (_) {}
    };
    tick();
    return () => { alive = false; };
  }, [job?.id]);
  if (!job) return null;
  const pct = detail?.total > 0 ? Math.round((detail.processed / detail.total) * 100) : (detail?.status === "done" ? 100 : 0);
  return h(Dialog, { open: !!job, onOpenChange: onClose },
    h(DialogContent, { className: "max-w-2xl max-h-[85vh] overflow-y-auto", "data-testid": "job-details-dialog" },
      h(DialogHeader, null, h(DialogTitle, null, "Upload Job Details \u2014 ", h("span", { className: "text-xs font-mono text-slate-500" }, job.id.slice(0, 8)))),
      !detail
        ? h("p", { className: "text-sm text-slate-500 py-6 text-center" }, "Loading\u2026")
        : h("div", { className: "space-y-4" },
            h("div", { className: "p-3 rounded-lg bg-slate-50" },
              h("p", { className: "text-xs text-slate-600" }, "Session: ", h("b", null, job.session_name || detail.session_id)),
              h("p", { className: "text-[11px] text-slate-500 mt-0.5" }, "Started: ", fmtDT(detail.created_at)),
              detail.completed_at && h("p", { className: "text-[11px] text-slate-500" }, "Completed: ", fmtDT(detail.completed_at))
            ),
            h("div", null,
              h("div", { className: "flex items-center justify-between text-xs mb-1" },
                h("span", null, "Status: ", h("b", null, detail.status)),
                h("span", { className: "font-mono" }, detail.processed + " / " + detail.total + " (" + pct + "%)")
              ),
              h("div", { className: "h-2 bg-slate-200 rounded overflow-hidden" },
                h("div", { className: "h-full bg-blue-500 transition-all", style: { width: pct + "%" } })
              )
            ),
            h("div", { className: "grid grid-cols-3 gap-2 text-center" },
              h("div", { className: "p-2 rounded bg-emerald-50 border border-emerald-100" },
                h("p", { className: "text-[10px] uppercase text-emerald-700" }, "Indexed"),
                h("p", { className: "text-lg font-bold text-emerald-700" }, detail.indexed_count || 0)
              ),
              h("div", { className: "p-2 rounded bg-rose-50 border border-rose-100" },
                h("p", { className: "text-[10px] uppercase text-rose-700" }, "Skipped"),
                h("p", { className: "text-lg font-bold text-rose-700" }, detail.skipped_count || 0)
              ),
              h("div", { className: "p-2 rounded bg-slate-50 border border-slate-200" },
                h("p", { className: "text-[10px] uppercase text-slate-500" }, "Applicants"),
                h("p", { className: "text-lg font-bold text-slate-700" }, detail.total_applicants_in_excel || 0)
              )
            ),
            (detail.skipped || []).length > 0 && h("div", null,
              h("p", { className: "text-xs font-semibold text-slate-700 mb-1" }, "Skipped rows (" + detail.skipped.length + ")"),
              h("div", { className: "max-h-48 overflow-y-auto border rounded" },
                h("table", { className: "w-full text-xs" },
                  h("thead", null, h("tr", { className: "bg-slate-50 text-slate-500 text-[10px] uppercase" },
                    h("th", { className: "px-2 py-1 text-left" }, "Filename / Applicant"),
                    h("th", { className: "px-2 py-1 text-left" }, "Reason")
                  )),
                  h("tbody", null, detail.skipped.map((s, i) => h("tr", { key: i, className: "border-t" },
                    h("td", { className: "px-2 py-1 font-mono" }, s.filename),
                    h("td", { className: "px-2 py-1 text-slate-600" }, s.reason)
                  )))
                )
              )
            )
          ),
      h(DialogFooter, null, h(Button, { variant: "outline", onClick: onClose }, "Close"))
    )
  );
}

export function FaceManageKiosk() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [picker, setPicker] = useState(false);
  const [excel, setExcel] = useState(null);
  const [images, setImages] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [recentJobs, setRecentJobs] = useState([]);
  const [resumeJob, setResumeJob] = useState(null); // { id, ... } if tracking one

  const refreshSessions = useCallback(async () => {
    try { const r = await axios.get(API_URL + "/sessions"); setSessions(r.data.items || []); }
    catch (_) { setSessions([]); }
  }, []);
  const refreshRecentJobs = useCallback(async () => {
    try { const r = await axios.get(API_URL + "/upload-jobs?limit=10"); setRecentJobs(r.data.items || []); }
    catch (_) { setRecentJobs([]); }
  }, []);
  const refreshSession = useCallback(async (sid) => {
    try {
      const [s, c] = await Promise.all([
        axios.get(API_URL + "/sessions/" + sid),
        axios.get(API_URL + "/candidates?session_id=" + sid + "&limit=200"),
      ]);
      setActiveSession(s.data);
      setCandidates(c.data.items || []);
    } catch (_) {}
  }, []);
  useEffect(() => { refreshSessions(); refreshRecentJobs(); }, [refreshSessions, refreshRecentJobs]);
  // Auto-refresh the recent-uploads panel every 10s so operator sees live progress
  useEffect(() => {
    const iv = setInterval(refreshRecentJobs, 10000);
    return () => clearInterval(iv);
  }, [refreshRecentJobs]);

  const onSessionSelected = async (s) => {
    setPicker(false);
    await refreshSession(s.id);
    await refreshSessions();
  };

  const submit = async () => {
    if (!activeSession) { toast.error("Pick a session first"); return; }
    if (!excel || !images || images.length === 0) { toast.error("Choose Excel and photos"); return; }
    setSubmitting(true); setProgress(0); setResult(null);
    try {
      const fd = new FormData();
      fd.append("excel_file", excel);
      for (const f of images) fd.append("image_files", f);
      // Kick off the async job — this call returns quickly with a job_id
      const r = await axios.post(API_URL + "/upload-candidates?session_id=" + activeSession.id, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => { if (e.total) setProgress(Math.round((e.loaded * 100) / e.total)); },
        timeout: 300000,
      });
      const jobId = r.data.job_id;
      const total = r.data.total || 0;
      if (!jobId) {
        // Legacy synchronous response (shouldn't happen with new backend)
        setResult(r.data);
        toast.success((r.data.indexed_count || 0) + " applicants indexed");
      } else {
        // Poll for progress
        toast.success("Upload accepted \u2014 indexing " + total + " applicants in background");
        setProgress(0);
        let last = null;
        while (true) {
          await new Promise(res => setTimeout(res, 2000));
          try {
            const s = await axios.get(API_URL + "/upload-jobs/" + jobId);
            last = s.data;
            if (last.total > 0) setProgress(Math.round((last.processed / last.total) * 100));
            if (last.status === "done" || last.status === "error") break;
          } catch (_) { /* keep polling, may be a transient blip */ }
        }
        if (last?.status === "error") {
          toast.error("Indexing failed: " + (last.error || "unknown"));
        } else {
          setResult({
            indexed_count: last?.indexed_count || 0,
            skipped_count: last?.skipped_count || 0,
            indexed: last?.indexed || [],
            skipped: last?.skipped || [],
            total_applicants_in_excel: last?.total_applicants_in_excel || 0,
          });
          toast.success((last?.indexed_count || 0) + " applicants indexed");
        }
      }
      setExcel(null); setImages([]);
      await refreshSession(activeSession.id);
      await refreshSessions();
      await refreshRecentJobs();
    } catch (err) { toast.error(err.response?.data?.detail || err.message || "Upload failed"); }
    finally { setSubmitting(false); }
  };

  const deleteSession = async () => {
    if (!activeSession) return;
    setConfirmDelete(false);
    try {
      const r = await axios.delete(API_URL + "/sessions/" + activeSession.id);
      toast.success("Session deleted (" + r.data.candidates_deleted + " applicants, " + r.data.scans_deleted + " scans)");
      setActiveSession(null); setCandidates([]); setResult(null);
      await refreshSessions();
      setPicker(true);
    } catch (err) { toast.error("Could not delete session"); }
  };

  const phaseColors = {
    active: "bg-emerald-100 text-emerald-700 border-emerald-200",
    scheduled: "bg-amber-100 text-amber-700 border-amber-200",
    ended: "bg-slate-100 text-slate-500 border-slate-200",
  };

  const tableHead = h("thead", null, h("tr", { className: "text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50" },
    h("th", { className: "px-3 py-2" }, "Application No"),
    h("th", { className: "px-3 py-2" }, "Name"),
    h("th", { className: "px-3 py-2" }, "Category"),
    h("th", { className: "px-3 py-2" }, "Mobile"),
    h("th", { className: "px-3 py-2" }, "Email"),
    h("th", { className: "px-3 py-2" }, "Experience"),
    h("th", { className: "px-3 py-2" }, "Indexed")
  ));
  const tableBody = h("tbody", { className: "divide-y divide-slate-100" }, candidates.map(c =>
    h("tr", { key: c.application_no, "data-testid": "cand-row-" + c.application_no },
      h("td", { className: "px-3 py-2 font-mono text-xs" }, c.application_no),
      h("td", { className: "px-3 py-2 font-medium text-slate-800" }, c.name),
      h("td", { className: "px-3 py-2 text-xs" }, c.category || "\u2014"),
      h("td", { className: "px-3 py-2 text-xs" }, c.mobile || "\u2014"),
      h("td", { className: "px-3 py-2 text-xs truncate max-w-[180px]" }, c.email || "\u2014"),
      h("td", { className: "px-3 py-2 text-xs" }, c.total_experience || "\u2014"),
      h("td", { className: "px-3 py-2 text-xs text-slate-500" }, new Date(c.indexed_at).toLocaleString("en-IN"))
    )
  ));

  return h("div", { className: "min-h-screen bg-slate-100" },
    h(BrandHeader, {
      subtitle: "Manage exam sessions \u00B7 Upload rosters \u00B7 Reports",
      action: h("div", { className: "flex items-center gap-2" },
        h(Button, { variant: "secondary", size: "sm", onClick: () => setPicker(true), className: "bg-amber-400 hover:bg-amber-500 text-slate-900", "data-testid": "switch-session-manage" },
          h(Calendar, { className: "w-3 h-3 mr-1" }), "Switch Session"
        ),
        h(Button, { variant: "secondary", size: "sm", onClick: () => navigate("/exam-entry/face-verify"), className: "bg-slate-700 hover:bg-slate-600 text-white" },
          h(ChevronLeft, { className: "w-3 h-3 mr-1" }), "Back to Kiosk"
        )
      )
    }),
    h("main", { className: "max-w-7xl mx-auto px-6 py-8 space-y-6" },
      // Sessions table
      h(Card, null, h(CardContent, { className: "p-6" },
        h("div", { className: "flex items-center justify-between mb-3" },
          h("div", null,
            h("h2", { className: "text-lg font-bold text-slate-900 flex items-center gap-2" }, h(Calendar, { className: "w-5 h-5 text-amber-500" }), "Exam Sessions"),
            h("p", { className: "text-xs text-slate-500" }, "Every roster and scan log is scoped to a session. Scanning is only allowed within a session's time window.")
          ),
          h(Button, { onClick: () => setPicker(true), className: "bg-amber-400 hover:bg-amber-500 text-slate-900", "data-testid": "open-sessions-btn" },
            h(Plus, { className: "w-4 h-4 mr-1" }), "New / Switch Session"
          )
        ),
        sessions.length === 0
          ? h("p", { className: "text-sm text-slate-500 italic" }, "No sessions yet.")
          : h("div", { className: "overflow-x-auto" }, h("table", { className: "w-full text-sm" },
              h("thead", null, h("tr", { className: "text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50" },
                h("th", { className: "px-3 py-2" }, "Name"),
                h("th", { className: "px-3 py-2" }, "Window"),
                h("th", { className: "px-3 py-2" }, "Phase"),
                h("th", { className: "px-3 py-2 text-center" }, "Roster"),
                h("th", { className: "px-3 py-2 text-center" }, "Scans"),
                h("th", { className: "px-3 py-2" }, "")
              )),
              h("tbody", { className: "divide-y divide-slate-100" }, sessions.map(s =>
                h("tr", { key: s.id, className: "hover:bg-slate-50 cursor-pointer", onClick: () => onSessionSelected(s), "data-testid": "session-row-" + s.id },
                  h("td", { className: "px-3 py-2 font-semibold" }, s.name),
                  h("td", { className: "px-3 py-2 text-xs text-slate-500" }, fmtDT(s.start_at) + " \u2192 " + fmtDT(s.end_at)),
                  h("td", { className: "px-3 py-2" }, h("span", { className: "text-[10px] uppercase px-2 py-0.5 rounded-full border " + (phaseColors[s.phase] || "") }, s.phase)),
                  h("td", { className: "px-3 py-2 text-center text-xs font-mono" }, s.roster_count),
                  h("td", { className: "px-3 py-2 text-center text-xs" }, s.scans_matched + " / " + s.scans_total),
                  h("td", { className: "px-3 py-2 text-right" },
                    h(Link, { to: "/exam-entry/report/" + s.id, onClick: (e) => e.stopPropagation(), className: "text-xs text-slate-600 hover:underline inline-flex items-center gap-1 mr-3", "data-testid": "report-link-" + s.id },
                      h(BarChart3, { className: "w-3 h-3" }), "Report"
                    )
                  )
                )
              ))
            ))
      )),

      // Recent Uploads panel — persistent across sessions/tabs
      h(Card, null, h(CardContent, { className: "p-6" },
        h("div", { className: "flex items-center justify-between mb-3" },
          h("div", null,
            h("h2", { className: "text-lg font-bold text-slate-900 flex items-center gap-2" }, h(Upload, { className: "w-5 h-5 text-blue-500" }), "Recent Uploads"),
            h("p", { className: "text-xs text-slate-500" }, "Last 10 photo-indexing jobs across all sessions. Refreshes every 10s.")
          ),
          h(Button, { variant: "outline", size: "sm", onClick: refreshRecentJobs, "data-testid": "refresh-jobs-btn" },
            h(RefreshCw, { className: "w-3 h-3 mr-1" }), "Refresh"
          )
        ),
        recentJobs.length === 0
          ? h("p", { className: "text-sm text-slate-500 italic text-center py-6" }, "No uploads yet.")
          : h("div", { className: "overflow-x-auto" }, h("table", { className: "w-full text-sm" },
              h("thead", null, h("tr", { className: "text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50" },
                h("th", { className: "px-3 py-2" }, "Session"),
                h("th", { className: "px-3 py-2" }, "Started"),
                h("th", { className: "px-3 py-2" }, "Status"),
                h("th", { className: "px-3 py-2" }, "Progress"),
                h("th", { className: "px-3 py-2 text-center" }, "Indexed / Total"),
                h("th", { className: "px-3 py-2 text-center" }, "Skipped"),
                h("th", { className: "px-3 py-2 text-right" }, "")
              )),
              h("tbody", { className: "divide-y divide-slate-100" }, recentJobs.map(j => {
                const pct = j.total > 0 ? Math.round((j.processed / j.total) * 100) : (j.status === "done" ? 100 : 0);
                const badge = j.status === "done" ? "bg-emerald-100 text-emerald-700"
                  : j.status === "running" ? "bg-blue-100 text-blue-700"
                  : j.status === "queued" ? "bg-amber-100 text-amber-700"
                  : j.status === "error" ? "bg-rose-100 text-rose-700" : "bg-slate-100 text-slate-600";
                return h("tr", { key: j.id, "data-testid": "recent-job-" + j.id, className: "hover:bg-slate-50" },
                  h("td", { className: "px-3 py-2" },
                    j.session_deleted
                      ? h("span", { className: "text-rose-500 italic text-xs" }, "(session deleted)")
                      : h("div", null,
                          h("p", { className: "font-medium text-slate-900 text-xs" }, j.session_name || "\u2014"),
                          j.position_name && h("p", { className: "text-[10px] text-slate-500" }, j.position_name)
                        )
                  ),
                  h("td", { className: "px-3 py-2 text-[11px] text-slate-500" }, fmtDT(j.created_at)),
                  h("td", { className: "px-3 py-2" }, h(Badge, { className: badge + " text-[10px] uppercase tracking-wider" }, j.status)),
                  h("td", { className: "px-3 py-2" },
                    h("div", { className: "flex items-center gap-2" },
                      h("div", { className: "flex-1 h-1.5 bg-slate-200 rounded overflow-hidden min-w-[80px]" },
                        h("div", { className: "h-full bg-blue-500 transition-all", style: { width: pct + "%" } })
                      ),
                      h("span", { className: "text-[10px] font-mono text-slate-500 w-8 text-right" }, pct + "%")
                    )
                  ),
                  h("td", { className: "px-3 py-2 text-center text-xs font-mono" }, (j.indexed_count || 0) + " / " + (j.total || 0)),
                  h("td", { className: "px-3 py-2 text-center text-xs font-mono text-rose-600" }, j.skipped_count || 0),
                  h("td", { className: "px-3 py-2 text-right" },
                    h(Button, {
                      size: "sm", variant: "ghost", className: "text-xs",
                      onClick: () => setResumeJob({ id: j.id, session_name: j.session_name }),
                      "data-testid": "resume-job-" + j.id
                    }, "Details")
                  )
                );
              }))
            ))
      )),

      // Active session upload + roster
      activeSession && h(Card, null, h(CardContent, { className: "p-6" },
        h("div", { className: "flex items-center justify-between mb-4" },
          h("div", null,
            h("h2", { className: "text-lg font-bold text-slate-900" }, "Upload Applicants \u2014 ", h("span", { className: "text-amber-600" }, activeSession.name)),
            h("p", { className: "text-xs text-slate-500" },
              "Excel: ", h("code", { className: "px-1 bg-slate-100 rounded" }, "applications_data.xlsx"),
              ". Photo filenames must start with the ", h("code", { className: "px-1 bg-slate-100 rounded" }, "Application No"),
              " (e.g. ", h("code", { className: "px-1 bg-slate-100 rounded" }, "APP-2026-000317.jpg"), ")."
            )
          ),
          h(Badge, { variant: "outline", className: "text-sm bg-emerald-50 text-emerald-700 border-emerald-200", "data-testid": "manage-roster-count" }, activeSession.roster_count + " loaded")
        ),
        h("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-4" },
          h("label", { className: "block" },
            h("span", { className: "text-xs font-semibold uppercase tracking-wider text-slate-600" }, "Applications Excel (.xlsx)"),
            h("input", {
              type: "file", accept: ".xlsx,.xls", onChange: e => setExcel(e.target.files?.[0] || null), disabled: submitting,
              "data-testid": "manage-excel-input",
              className: "mt-1.5 block w-full text-sm border border-slate-300 rounded-md p-2 file:mr-3 file:px-3 file:py-1 file:rounded file:border-0 file:bg-slate-900 file:text-white file:text-xs"
            }),
            excel && h("p", { className: "text-xs text-emerald-700 mt-1" }, "\u2713 ", excel.name)
          ),
          h("label", { className: "block" },
            h("span", { className: "text-xs font-semibold uppercase tracking-wider text-slate-600" }, "Candidate Photos \u2014 select folder"),
            h("input", {
              type: "file", accept: "image/*", multiple: true, onChange: e => setImages(Array.from(e.target.files || [])), disabled: submitting,
              "data-testid": "manage-images-input",
              className: "mt-1.5 block w-full text-sm border border-slate-300 rounded-md p-2 file:mr-3 file:px-3 file:py-1 file:rounded file:border-0 file:bg-slate-900 file:text-white file:text-xs"
            }),
            images.length > 0 && h("p", { className: "text-xs text-emerald-700 mt-1" }, "\u2713 ", images.length, " files")
          )
        ),
        h("div", { className: "mt-5 flex items-center gap-3 flex-wrap" },
          h(Button, { onClick: submit, disabled: submitting || !excel || images.length === 0, className: "bg-slate-900 hover:bg-slate-800", "data-testid": "manage-upload-btn" },
            h(Upload, { className: "w-4 h-4 mr-1.5" }), submitting ? ("Uploading\u2026 " + progress + "%") : "Upload & Index Faces"
          ),
          h(Link, { to: "/exam-entry/report/" + activeSession.id, className: "text-xs text-slate-600 hover:underline inline-flex items-center gap-1", "data-testid": "open-report" },
            h(BarChart3, { className: "w-3 h-3" }), "Open Report"
          ),
          h("a", { href: API_URL + "/scan-logs/export?session_id=" + activeSession.id, target: "_blank", rel: "noreferrer", className: "text-xs text-slate-600 hover:underline inline-flex items-center gap-1", "data-testid": "manage-export-csv" },
            h(FileDown, { className: "w-3 h-3" }), "Export Scan Log"
          ),
          h("span", { className: "flex-1" }),
          !confirmDelete && h(Button, { variant: "ghost", size: "sm", className: "text-rose-600 hover:text-rose-700", onClick: () => setConfirmDelete(true), "data-testid": "delete-session-btn" },
            h(Trash2, { className: "w-3 h-3 mr-1" }), "Delete Session"
          ),
          confirmDelete && h("div", { className: "flex items-center gap-2" },
            h("span", { className: "text-xs text-rose-700 font-medium" }, "Permanently delete this session, its roster, and all scan logs?"),
            h(Button, { size: "sm", variant: "destructive", onClick: deleteSession, "data-testid": "delete-session-confirm" }, "Yes, delete"),
            h(Button, { size: "sm", variant: "outline", onClick: () => setConfirmDelete(false) }, "Cancel")
          )
        ),
        result && h("div", { className: "mt-5 p-4 bg-slate-50 border border-slate-200 rounded-lg", "data-testid": "manage-upload-result" },
          h("p", { className: "text-sm font-semibold text-slate-800" }, "Batch summary"),
          h("p", { className: "text-xs text-slate-600 mt-1" },
            "Indexed: ", h("b", { className: "text-emerald-700" }, result.indexed_count),
            " of ", h("b", null, result.total_applicants_in_excel || "?"),
            " applicants \u00B7 Skipped: ", h("b", { className: "text-rose-700" }, result.skipped_count)
          ),
          result.skipped?.length > 0 && h("details", { className: "mt-2" },
            h("summary", { className: "text-xs text-slate-500 cursor-pointer" }, "View skipped (", result.skipped.length, ")"),
            h("ul", { className: "mt-1 text-xs text-slate-600 list-disc list-inside max-h-40 overflow-auto" },
              result.skipped.map((s, i) => h("li", { key: i }, s.filename, " \u2014 ", h("span", { className: "text-rose-600" }, s.reason)))
            )
          )
        )
      )),

      activeSession && h(Card, null, h(CardContent, { className: "p-6" },
        h("div", { className: "flex items-center justify-between mb-3" },
          h("h2", { className: "text-lg font-bold text-slate-900" }, "Indexed Applicants \u2014 ", h("span", { className: "text-slate-500 font-normal" }, activeSession.name)),
          h(Button, { variant: "ghost", size: "sm", onClick: () => refreshSession(activeSession.id) }, h(RefreshCw, { className: "w-3 h-3 mr-1" }), "Refresh")
        ),
        candidates.length === 0
          ? h("p", { className: "text-sm text-slate-500 italic" }, "No applicants indexed yet for this session.")
          : h("div", { className: "overflow-x-auto" }, h("table", { className: "w-full text-sm" }, tableHead, tableBody))
      ))
    ),
    h(SessionPickerDialog, { open: picker, onClose: () => setPicker(false), onSelected: onSessionSelected, mode: "manage" }),
    // Job details dialog
    h(JobDetailsDialog, { job: resumeJob, onClose: () => setResumeJob(null) })
  );
}

// ============================================================================
// Session Report screen
// ============================================================================
export function FaceSessionReport() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("present");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(API_URL + "/sessions/" + sessionId + "/report")
      .then(r => setData(r.data))
      .catch(() => toast.error("Could not load report"))
      .finally(() => setLoading(false));
  }, [sessionId]);

  if (loading) return h("div", { className: "min-h-screen bg-slate-100 flex items-center justify-center" }, "Loading report\u2026");
  if (!data) return h("div", { className: "min-h-screen bg-slate-100 flex items-center justify-center" }, "Report not available");

  const s = data.session;
  const list = tab === "present" ? data.present : tab === "absent" ? data.absent : data.unmatched_scans;

  return h("div", { className: "min-h-screen bg-slate-100" },
    h(BrandHeader, {
      subtitle: "Attendance Report \u00B7 " + s.name,
      action: h(Button, { variant: "secondary", size: "sm", onClick: () => navigate("/exam-entry/manage"), className: "bg-amber-400 hover:bg-amber-500 text-slate-900" },
        h(ChevronLeft, { className: "w-3 h-3 mr-1" }), "Back to Manage"
      )
    }),
    h("main", { className: "max-w-7xl mx-auto px-6 py-8 space-y-6" },
      h(Card, null, h(CardContent, { className: "p-6" },
        h("div", { className: "grid grid-cols-2 md:grid-cols-4 gap-3" },
          h(KPI, { label: "Roster Size", value: data.total_roster, color: "slate" }),
          h(KPI, { label: "Present (Matched)", value: data.total_present, color: "emerald" }),
          h(KPI, { label: "Absent (Not Scanned)", value: data.total_absent, color: "amber" }),
          h(KPI, { label: "Unrecognised Scans", value: data.unmatched_scans.length, color: "rose" })
        ),
        h("div", { className: "mt-3 text-xs text-slate-500" }, fmtDT(s.start_at) + " \u2192 " + fmtDT(s.end_at), " \u00B7 Phase: ", h("b", null, s.phase))
      )),

      h(Card, null, h(CardContent, { className: "p-6" },
        h("div", { className: "flex items-center justify-between mb-3 flex-wrap gap-2" },
          h("div", { className: "flex items-center gap-2" },
            h(TabBtn, { active: tab === "present", onClick: () => setTab("present"), label: "Present (" + data.total_present + ")", testId: "tab-present" }),
            h(TabBtn, { active: tab === "absent", onClick: () => setTab("absent"), label: "Absent (" + data.total_absent + ")", testId: "tab-absent" }),
            h(TabBtn, { active: tab === "unmatched", onClick: () => setTab("unmatched"), label: "Unrecognised (" + data.unmatched_scans.length + ")", testId: "tab-unmatched" })
          ),
          h("a", { href: API_URL + "/scan-logs/export?session_id=" + sessionId, target: "_blank", rel: "noreferrer", className: "text-xs text-slate-600 hover:underline inline-flex items-center gap-1" },
            h(FileDown, { className: "w-3 h-3" }), "Download Full Scan Log (CSV)"
          )
        ),
        list.length === 0
          ? h("p", { className: "text-sm text-slate-500 italic text-center py-12" }, "Nothing to show in this tab.")
          : h("div", { className: "overflow-x-auto" }, h("table", { className: "w-full text-sm" },
              tab === "unmatched"
                ? h("thead", null, h("tr", { className: "text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50" },
                    h("th", { className: "px-3 py-2" }, "Time"), h("th", { className: "px-3 py-2" }, "Scan ID")))
                : h("thead", null, h("tr", { className: "text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50" },
                    h("th", { className: "px-3 py-2" }, "Application No"),
                    h("th", { className: "px-3 py-2" }, "Name"),
                    h("th", { className: "px-3 py-2" }, "Category"),
                    h("th", { className: "px-3 py-2" }, "Mobile"),
                    tab === "present" && h("th", { className: "px-3 py-2" }, "Verified At"),
                    tab === "present" && h("th", { className: "px-3 py-2" }, "Similarity")
                  )),
              h("tbody", { className: "divide-y divide-slate-100" }, list.map((r, i) =>
                tab === "unmatched"
                  ? h("tr", { key: r.scan_id || i }, h("td", { className: "px-3 py-2 text-xs" }, fmtDT(r.scan_timestamp)), h("td", { className: "px-3 py-2 font-mono text-[10px] text-slate-400" }, r.scan_id))
                  : h("tr", { key: r.application_no, className: "hover:bg-slate-50" },
                      h("td", { className: "px-3 py-2 font-mono text-xs" }, r.application_no),
                      h("td", { className: "px-3 py-2 font-medium" }, r.name),
                      h("td", { className: "px-3 py-2 text-xs" }, r.category || "\u2014"),
                      h("td", { className: "px-3 py-2 text-xs" }, r.mobile || "\u2014"),
                      tab === "present" && h("td", { className: "px-3 py-2 text-xs text-emerald-700" }, fmtDT(r.verified_at)),
                      tab === "present" && h("td", { className: "px-3 py-2 text-xs" }, r.similarity != null ? r.similarity.toFixed(2) + "%" : "\u2014")
                    )
              ))
            ))
      ))
    )
  );
}

const KPI = ({ label, value, color }) => {
  const colors = {
    slate: "bg-slate-50 text-slate-700 border-slate-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    rose: "bg-rose-50 text-rose-700 border-rose-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
  };
  return h("div", { className: "p-4 rounded-xl border " + colors[color] },
    h("p", { className: "text-[10px] uppercase tracking-wider font-semibold opacity-75" }, label),
    h("p", { className: "text-3xl font-bold mt-1" }, (value ?? 0).toLocaleString())
  );
};

const TabBtn = ({ active, onClick, label, testId }) => h("button", {
  onClick,
  className: "px-3 py-1.5 rounded-lg text-sm font-medium transition-colors " + (active ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"),
  "data-testid": testId
}, label);
